import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Sélecteur de pack (Mproteine).
 *
 * Étape 1 : quantité (radios natifs). Étape 2 : goûts.
 *  - 1 sachet     : combobox « select-only » (liste déroulante simple, clavier complet).
 *  - 2 sachets et + : mix & match, compteurs +/- plafonnés au nombre de sachets du pack.
 *
 * Prix affiché : prix/sachet du pack atteint × quantité, miroir des remises automatiques.
 * Ajout panier : 1 POST items[] + CartLinesUpdateEvent (le cart drawer Horizon s'ouvre).
 */
class PackSelector extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  #packQty = 1;
  /** Quantité maximum du pack choisi (= #packQty, sauf pack ouvert « 3+ »). */
  #packMax = 1;
  /** Variantes choisies, dans l'ordre d'ajout : si le pack rétrécit, les derniers ajouts partent en premier. */
  #picks = [];
  #single = '';
  #activeIndex = -1;
  #busy = false;
  /** @type {Map<HTMLElement, number>} */
  #settleTimers = new Map();

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}}€';
    this.currency = this.dataset.currency || 'EUR';
    this.unitSingular = this.dataset.unitSingular || 'sachet';
    this.unitPlural = this.dataset.unitPlural || 'sachets';
    /** Goût unique : une seule variante, la quantité ajoutée = le pack choisi. */
    this.singleFlavor = this.dataset.singleFlavor === 'true';

    this.refs = {
      radios: [...this.querySelectorAll('.ps-pack input[type="radio"]')],
      panels: {
        single: this.querySelector('[data-panel="single"]'),
        multi: this.querySelector('[data-panel="multi"]'),
      },
      select: this.querySelector('.ps-select'),
      trigger: this.querySelector('[ref="trigger"]'),
      triggerValue: this.querySelector('[ref="triggerValue"]'),
      listbox: this.querySelector('[ref="listbox"]'),
      options: [...this.querySelectorAll('[role="option"]')],
      rows: [...this.querySelectorAll('.ps-row')],
      submit: this.querySelector('[ref="submit"]'),
      submitLabel: this.querySelector('[ref="submitLabel"]'),
      submitSep: this.querySelector('[ref="submitSep"]'),
      submitPrices: this.querySelector('[ref="submitPrices"]'),
      oldPrice: this.querySelector('[ref="oldPrice"]'),
      finalPrice: this.querySelector('[ref="finalPrice"]'),
      error: this.querySelector('[ref="error"]'),
    };

    /** @type {Map<string, {price: number, available: boolean}>} */
    this.variants = new Map(
      [...this.refs.rows, ...this.querySelectorAll('[data-ps-variant]')].map((row) => [
        row.dataset.variantId,
        { price: parseInt(row.dataset.price, 10) || 0, available: row.dataset.available === 'true' },
      ])
    );

    this.tiers = this.refs.radios
      .map((radio) => ({ qty: parseInt(radio.value, 10) || 0, unit: parseInt(radio.dataset.unitPrice, 10) || 0 }))
      .sort((a, b) => a.qty - b.qty);

    const checked = this.refs.radios.find((radio) => radio.checked) || this.refs.radios[0];
    ({ qty: this.#packQty, max: this.#packMax } = this.#readRadio(checked));
    this.#single = this.#isAvailable(this.dataset.singleVariant) ? this.dataset.singleVariant : this.#firstAvailable();

    this.addEventListener(
      'change',
      (event) => {
        const radio = event.target instanceof Element ? event.target.closest('.ps-pack input') : null;
        if (radio) this.#setPack(radio);
      },
      { signal }
    );
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.refs.trigger?.addEventListener('keydown', (event) => this.#onTriggerKeydown(event), { signal });
    // Garde le focus sur le déclencheur pendant un clic dans la liste (pattern combobox).
    this.refs.listbox?.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
    this.refs.listbox?.addEventListener(
      'mousemove',
      (event) => {
        const option = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
        const index = option ? this.refs.options.indexOf(option) : -1;
        if (index >= 0 && index !== this.#activeIndex && option.getAttribute('aria-disabled') !== 'true') {
          this.#highlight(index, false);
        }
      },
      { signal }
    );
    document.addEventListener(
      'click',
      (event) => {
        if (this.#isOpen && this.refs.select && !this.refs.select.contains(/** @type {Node} */ (event.target))) {
          this.#close();
        }
      },
      { signal }
    );

    this.#render();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    for (const timer of this.#settleTimers.values()) clearTimeout(timer);
    this.#settleTimers.clear();
  }

  /* ------------------------------------------------------------------ état */

  get #mode() {
    return this.#packQty > 1 ? 'multi' : 'single';
  }

  #isAvailable(id) {
    return Boolean(id) && this.variants.get(String(id))?.available === true;
  }

  #firstAvailable() {
    for (const [id, variant] of this.variants) if (variant.available) return id;
    return '';
  }

  #count(id) {
    let n = 0;
    for (const pick of this.#picks) if (pick === id) n += 1;
    return n;
  }

  /** Quantité mini et maxi d'un pack (pack ouvert : de la quantité affichée jusqu'au maximum réglé). */
  #readRadio(radio) {
    const qty = radio ? parseInt(radio.value, 10) || 1 : 1;
    const max = radio && radio.dataset.open === 'true' ? Math.max(qty, parseInt(radio.dataset.max, 10) || qty) : qty;
    return { qty, max };
  }

  #setPack(radio) {
    const previousMode = this.#mode;
    const { qty, max } = this.#readRadio(radio);
    this.#packQty = qty;
    this.#packMax = max;

    if (this.#mode === 'multi') {
      // En venant de la liste simple, le goût choisi devient le premier sachet du pack,
      // sauf s'il est déjà le dernier ajouté (on garde alors la composition en cours).
      if (previousMode === 'single' && this.#isAvailable(this.#single)) {
        const last = this.#picks[this.#picks.length - 1];
        if (last !== this.#single) this.#picks = [this.#single];
      }
      if (this.#picks.length > max) this.#picks.length = max;
    } else {
      const last = this.#picks[this.#picks.length - 1];
      if (this.#isAvailable(last)) this.#single = last;
    }

    this.#close();
    this.#render();
  }

  #items() {
    if (this.singleFlavor) {
      return this.#isAvailable(this.#single) ? [{ id: this.#single, quantity: this.#packQty }] : [];
    }
    if (this.#mode === 'single') {
      return this.#isAvailable(this.#single) ? [{ id: this.#single, quantity: 1 }] : [];
    }
    /** @type {Map<string, number>} */
    const counts = new Map();
    for (const id of this.#picks) counts.set(id, (counts.get(id) || 0) + 1);
    return [...counts].map(([id, quantity]) => ({ id, quantity }));
  }

  /** Prix/sachet du plus grand pack atteint par la quantité (0 = pas de prix saisi). */
  #unitPriceFor(quantity) {
    let unit = 0;
    for (const tier of this.tiers) if (quantity >= tier.qty) unit = tier.unit;
    return unit;
  }

  #money(cents) {
    const formatted = formatMoney(cents, this.moneyFormat, this.currency);
    return cents % 100 === 0 ? formatted.replace(/([.,])00\b/, '') : formatted;
  }

  /* ---------------------------------------------------------------- actions */

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const button = target.closest('button');

    if (button?.classList.contains('ps-stepper__btn')) {
      const row = button.closest('.ps-row');
      const id = row?.dataset.variantId;
      if (!id) return;
      if (button.dataset.action === 'inc') this.#increment(id, row);
      else this.#decrement(id, row);
      // Le bouton cliqué peut devenir inactif (pack plein, ou 0) : on garde le focus dans la ligne.
      if (button.disabled) {
        const sibling = row.querySelector(button.dataset.action === 'inc' ? '[data-action="dec"]' : '[data-action="inc"]');
        if (sibling instanceof HTMLButtonElement && !sibling.disabled) sibling.focus();
      }
      return;
    }

    if (button && button === this.refs.trigger) {
      if (this.#isOpen) this.#close();
      else this.#open();
      return;
    }

    if (button && button === this.refs.submit) {
      this.#addToCart();
      return;
    }

    const option = target.closest('[role="option"]');
    if (option && this.refs.listbox?.contains(option)) this.#choose(option);
  }

  #increment(id, row) {
    if (this.#picks.length >= this.#packMax || !this.#isAvailable(id)) return;
    this.#picks.push(id);
    this.#render();
    this.#bump(row);
  }

  #decrement(id, row) {
    const index = this.#picks.lastIndexOf(id);
    if (index < 0) return;
    this.#picks.splice(index, 1);
    this.#render();
    this.#bump(row);
  }

  #bump(row) {
    const value = row?.querySelector('.ps-stepper__value');
    if (!value) return;
    value.classList.remove('is-bump');
    void value.offsetWidth; // relance l'animation
    value.classList.add('is-bump');
  }

  /* ------------------------------------------------------ liste déroulante */

  get #isOpen() {
    return Boolean(this.refs.listbox && !this.refs.listbox.hidden);
  }

  #enabledIndexes() {
    return this.refs.options
      .map((option, index) => (option.getAttribute('aria-disabled') === 'true' ? -1 : index))
      .filter((index) => index >= 0);
  }

  #open() {
    const { listbox, trigger, select } = this.refs;
    if (!listbox || !trigger || this.#mode !== 'single') return;
    listbox.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    select?.classList.add('is-open');
    const selected = this.refs.options.findIndex((option) => option.dataset.variantId === this.#single);
    this.#highlight(selected >= 0 ? selected : (this.#enabledIndexes()[0] ?? -1));
  }

  #close(returnFocus = false) {
    const { listbox, trigger, select } = this.refs;
    if (!listbox || listbox.hidden) return;
    listbox.hidden = true;
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.removeAttribute('aria-activedescendant');
    select?.classList.remove('is-open');
    this.#highlight(-1);
    if (returnFocus) trigger?.focus();
  }

  #highlight(index, scroll = true) {
    this.#activeIndex = index;
    this.refs.options.forEach((option, i) => option.classList.toggle('is-active', i === index));
    const option = this.refs.options[index];
    if (!option) return;
    this.refs.trigger?.setAttribute('aria-activedescendant', option.id);
    if (!scroll || !this.refs.listbox) return;
    const list = this.refs.listbox;
    const top = option.offsetTop;
    const bottom = top + option.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top - 6;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight + 6;
  }

  #move(step) {
    const enabled = this.#enabledIndexes();
    if (!enabled.length) return;
    const position = enabled.indexOf(this.#activeIndex);
    const next = position < 0 ? (step > 0 ? 0 : enabled.length - 1) : Math.min(enabled.length - 1, Math.max(0, position + step));
    this.#highlight(enabled[next]);
  }

  #onTriggerKeydown(event) {
    const open = this.#isOpen;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (open) this.#move(event.key === 'ArrowDown' ? 1 : -1);
        else this.#open();
        break;
      case 'Home':
      case 'End':
        if (open) {
          event.preventDefault();
          const enabled = this.#enabledIndexes();
          this.#highlight(event.key === 'Home' ? enabled[0] : enabled[enabled.length - 1]);
        }
        break;
      case 'Enter':
      case ' ':
        // Fermée : le clic natif du bouton ouvre la liste.
        if (open) {
          event.preventDefault();
          const option = this.refs.options[this.#activeIndex];
          if (option) this.#choose(option);
          else this.#close(true);
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          this.#close(true);
        }
        break;
      case 'Tab':
        if (open) this.#close();
        break;
      default:
        break;
    }
  }

  #choose(option) {
    if (option.getAttribute('aria-disabled') === 'true') return;
    this.#single = option.dataset.variantId || '';
    this.#close(true);
    this.#render();
  }

  /* ----------------------------------------------------------------- rendu */

  #renderPanels() {
    for (const [name, panel] of Object.entries(this.refs.panels)) {
      if (!panel) continue;
      const open = name === this.#mode;
      if (panel.classList.contains('is-open') === open) continue;

      clearTimeout(this.#settleTimers.get(panel));
      panel.classList.remove('is-settled');
      panel.classList.toggle('is-open', open);
      panel.inert = !open;
      if (open) {
        // Après le dépliage, on autorise le débordement (la liste déroulante passe par-dessus).
        this.#settleTimers.set(
          panel,
          setTimeout(() => panel.classList.add('is-settled'), 360)
        );
      }
    }
  }

  #render() {
    const mode = this.#mode;
    const packQty = this.#packQty;
    const picked = this.#picks.length;
    const full = picked >= this.#packMax;

    for (const radio of this.refs.radios) {
      radio.closest('.ps-pack')?.classList.toggle('is-checked', radio.checked);
    }

    this.#renderPanels();

    // Lignes mix & match
    for (const row of this.refs.rows) {
      const id = row.dataset.variantId;
      const count = this.#count(id);
      row.classList.toggle('is-selected', count > 0);
      const value = row.querySelector('.ps-stepper__value');
      if (value) value.textContent = String(count);
      const dec = row.querySelector('[data-action="dec"]');
      const inc = row.querySelector('[data-action="inc"]');
      if (dec instanceof HTMLButtonElement) dec.disabled = count === 0;
      if (inc instanceof HTMLButtonElement) inc.disabled = !this.#isAvailable(id) || full;
    }

    // Liste simple
    for (const option of this.refs.options) {
      option.setAttribute('aria-selected', String(option.dataset.variantId === this.#single));
    }
    const { triggerValue } = this.refs;
    if (triggerValue && this.#single && triggerValue.dataset.variantId !== this.#single) {
      const chosen = this.refs.options.find((option) => option.dataset.variantId === this.#single);
      const flavor = chosen?.querySelector('.ps-flavor')?.cloneNode(true);
      if (flavor) {
        triggerValue.replaceChildren(flavor);
        triggerValue.dataset.variantId = this.#single;
      }
    }

    this.#renderSubmit();
  }

  #renderSubmit() {
    const { submit, submitLabel, submitSep, submitPrices, oldPrice, finalPrice } = this.refs;
    if (!submit) return;

    const mode = this.#mode;
    const items = this.#items();
    const quantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const complete = mode === 'single' ? quantity === 1 : quantity >= this.#packQty && quantity <= this.#packMax;

    if (!this.#busy) submit.disabled = !complete;

    if (!complete) {
      if (submitLabel) {
        if (mode === 'multi' && !this.singleFlavor) {
          const remaining = this.#packQty - quantity;
          const label = `${remaining} ${remaining > 1 ? this.unitPlural : this.unitSingular}`;
          submitLabel.textContent = (this.dataset.remainingText || 'Encore [nombre] à choisir').replace('[nombre]', label);
        } else {
          submitLabel.textContent = this.dataset.chooseText || 'Choisis ton goût';
        }
      }
      if (submitSep) submitSep.hidden = true;
      if (submitPrices) submitPrices.hidden = true;
      return;
    }

    let before = 0;
    for (const item of items) before += (this.variants.get(item.id)?.price || 0) * item.quantity;
    const unit = this.#unitPriceFor(quantity);
    const final = unit > 0 ? Math.min(before, unit * quantity) : before;

    if (submitLabel) submitLabel.textContent = this.dataset.addText || 'Ajouter au panier';
    if (submitSep) submitSep.hidden = false;
    if (submitPrices) submitPrices.hidden = false;
    if (finalPrice) finalPrice.textContent = this.#money(final);
    if (oldPrice) {
      const discounted = before > final;
      oldPrice.hidden = !discounted;
      oldPrice.textContent = discounted ? this.#money(before) : '';
    }
  }

  /* ------------------------------------------------------------ ajout panier */

  async #addToCart() {
    const items = this.#items();
    const { submit, error } = this.refs;
    if (!items.length || this.#busy || !submit) return;

    this.#busy = true;
    submit.disabled = true;
    submit.classList.add('is-loading');
    submit.setAttribute('aria-busy', 'true');
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => element instanceof HTMLElement ? element.dataset.sectionId : '')
      .filter(Boolean);
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'pack-selector',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items.map((item) => ({ id: Number(item.id), quantity: item.quantity })),
          sections: sectionIds.join(','),
        }),
      });
      const added = await response.json();

      if (!response.ok || added.status) {
        const message = added.description || added.message || 'Impossible d’ajouter ce pack au panier.';
        if (error) {
          error.textContent = message;
          error.hidden = false;
        }
        deferred.reject(new Error(message));
        return;
      }

      // cart/add.js ne renvoie pas le panier complet : createCartFromAjaxResponse en a besoin.
      const cart = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then(
        (res) => res.json()
      );

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: added.items,
          source: 'pack-selector',
          sourceId: this.id || 'pack-selector',
          itemCount,
          productId: this.dataset.productId,
          sections: added.sections,
          didError: false,
        },
      });
    } catch (exception) {
      if (error) {
        error.textContent = 'Une erreur est survenue. Réessaie dans un instant.';
        error.hidden = false;
      }
      deferred.reject(exception);
    } finally {
      this.#busy = false;
      submit.classList.remove('is-loading');
      submit.removeAttribute('aria-busy');
      this.#renderSubmit();
    }
  }
}

if (!customElements.get('pack-selector')) {
  customElements.define('pack-selector', PackSelector);
}
